import { Fragment, type ReactNode } from 'react';
import { cn } from '@shared/lib';

export interface SegmentedControlOption {
  label: ReactNode;
  value: string;
  /** Optional leading icon (Figma "Split evenly / by shares …"). */
  icon?: ReactNode;
  disabled?: boolean;
}

export type SegmentedControlVariant = 'filled' | 'raised' | 'boxed' | 'pillbtns';

export interface SegmentedControlProps {
  options: SegmentedControlOption[];
  /** Currently selected value (controlled). */
  value: string;
  onValueChange: (value: string) => void;
  /** Visual style — `filled` (default), `raised` (pill + white active), `boxed`, `pillbtns`. */
  variant?: SegmentedControlVariant;
  /** Accessible name for the group (required). */
  'aria-label': string;
  className?: string;
  buttonClassName?: string;
}

const container: Record<SegmentedControlVariant, string> = {
  filled: 'gap-0 rounded-lg border border-border bg-surface-raised p-0.5',
  raised: 'gap-1 rounded-full bg-accent-subtle p-1',
  boxed: 'gap-1 rounded-lg border border-border-soft bg-surface-raised p-1',
  pillbtns: 'gap-2 bg-transparent p-0',
};

const segment: Record<SegmentedControlVariant, { base: string; on: string; off: string }> = {
  filled: {
    base: 'px-5.5 py-4', // calculated dynamically
    on: 'bg-accent-soft font-semibold text-accent-deep',
    off: 'text-muted hover:text-fg',
  },
  raised: {
    base: 'px-4 py-0 rounded-full',
    on: 'bg-surface-raised font-semibold text-fg',
    off: 'text-muted cursor-pointer',
  },
  boxed: {
    base: 'rounded-lg',
    on: 'bg-accent-soft font-semibold text-fg px-4 py-2.5',
    off: 'text-fg/70 hover:text-fg px-2 py-2.5',
  },
  pillbtns: {
    base: 'h-7 px-4 text-xs font-semibold rounded-full border transition-all',
    on: 'bg-primary border-primary text-primary-fg hover:opacity-90',
    off: 'bg-surface-raised border-border text-fg/70 hover:bg-surface hover:text-fg',
  },
};

/**
 * Single-select segmented toggle (Figma "All time / This Month", "Password / QR Login",
 * "Split evenly / by shares …"). **A11y:** a real `radiogroup` of `radio`s with roving tab
 * order + Arrow-key navigation; the selected segment is `aria-checked`.
 */
export function SegmentedControl({
  options,
  value,
  onValueChange,
  variant = 'filled',
  className,
  buttonClassName,
  ...props
}: SegmentedControlProps) {
  const move = (dir: 1 | -1) => {
    const enabled = options.filter((o) => !o.disabled);
    const idx = enabled.findIndex((o) => o.value === value);
    if (idx === -1) return;
    const next = enabled[(idx + dir + enabled.length) % enabled.length];
    if (next) onValueChange(next.value);
  };

  const styles = segment[variant];

  return (
    <div
      role="radiogroup"
      aria-label={props['aria-label']}
      className={cn('inline-flex items-center overflow-hidden', container[variant], className)}
    >
      {options.map((option, i) => {
        const selected = option.value === value;

        let roundingClass = styles.base;
        if (variant === 'filled') {
          let extraRounding = 'rounded-none';
          if (selected) {
            if (options.length === 1) {
              extraRounding = 'rounded-md';
            } else if (i === 0) {
              extraRounding = 'rounded-l-md rounded-r-none';
            } else if (i === options.length - 1) {
              extraRounding = 'rounded-r-md rounded-l-none';
            }
          }
          roundingClass = cn(styles.base, extraRounding);
        }

        return (
          <Fragment key={option.value}>
            <button
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={option.disabled}
              tabIndex={selected ? 0 : -1}
              onClick={() => onValueChange(option.value)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                  e.preventDefault();
                  move(1);
                } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                  e.preventDefault();
                  move(-1);
                }
              }}
              className={cn(
                'inline-flex items-center justify-center gap-2 text-sm transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                'disabled:cursor-not-allowed disabled:opacity-50',
                roundingClass,
                selected ? styles.on : styles.off,
                buttonClassName,
              )}
            >
              {option.icon ? (
                <span className="flex shrink-0 items-center">{option.icon}</span>
              ) : null}
              {option.label}
            </button>
            {/* Show a divider for the filled variant between adjacent unselected options */}
            {variant === 'filled' && i < options.length - 1 && (
              <div
                className={cn(
                  'w-px h-4 self-center bg-divider transition-opacity duration-150',
                  selected || options[i + 1]?.value === value ? 'opacity-0' : 'opacity-100',
                )}
              />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}
